import {
  CSSObject,
  Divider,
  List,
  Theme,
  styled,
} from "@mui/material";

import MuiDrawer from "@mui/material/Drawer";
import sizeConfigs from "../../configs/sizeConfigs";
import appRoutes from "../../routes/appRoutes";
import SlidebarItemWithOutCollapse from "./SlidebarItemWithOutCollapse";
import SidebarItemCollapse from "./SidebarItemCollapse";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useAccessControl from "../hooks/useAccessControl";
import appAdminRoutes from "../../routes/appAdminRoutes";
import Cookies from "js-cookie";
import { useTheme } from "../hooks/useTheme";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

type Props = {
  handleScreen: () => void;
};



const openedMixin = (theme: Theme): CSSObject => ({
  width: sizeConfigs.sidebar.width,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "0",
  [theme.breakpoints.up("sm")]: {
    width: "0",
  },
});


const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: sizeConfigs.sidebar.width,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));



export const SideBar = ({ handleScreen }: Props) => {
  const { themeStyles } = useTheme();
  const { appState } = useSelector((state: RootState) => state.appState);
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(true);
  const { permissions }: any = useAccessControl();
  const [showSettingsButton, setShowSettingsButton] = useState<boolean>(true);
  const role = Cookies.get("role-jj-rental-ui") || '';
  const isAdmin = role.toLowerCase() === 'admin';


  const handleNavigation = () => {
    if (isAdmin || (permissions && permissions["adminsettings"])) {
      navigate("/admin");
      return;
    }

    const nextAvailableRoute = appAdminRoutes.find((route: any) => {
      const hasPermission = permissions ? permissions[route.section] : false;

      if (route.child) {
        const childRoute = route.child.find((child: any) => {
          const hasChildPermission = permissions ? permissions[child.section] : false;
          return hasChildPermission;
        });

        if (childRoute) {
          setShowSettingsButton(true);
          return true;
        } else {
          setShowSettingsButton(false);
        }
      }

      return hasPermission;
    });

    if (nextAvailableRoute) {
      const routeToNavigate = nextAvailableRoute.child?.find((child: any) => {
        return permissions && permissions[child.section];
      }) || nextAvailableRoute;

      const pathToNavigate = routeToNavigate.path ?? "/unauthorized";
      navigate(pathToNavigate);
    } else {

      navigate("/unauthorized");
    }
  };

  useEffect(() => {
    const hasChildRoutesWithPermission = appAdminRoutes.find((route: any) => {
      const hasPermission = permissions ? permissions[route.section] : false;

      if (route.child) {
        const childRoute = route.child.find((child: any) => {
          const hasChildPermission = permissions ? permissions[child.section] : false;
          return hasChildPermission;
        });

        return childRoute !== undefined;
      }

      return hasPermission;
    });

    setShowSettingsButton(hasChildRoutesWithPermission ? true : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, appAdminRoutes]);

  const pages = [
    "dashboarddailymanifest",
    "dashboardbookings",
    "dashboardavailability",
    "dashboardfleet",
    "dashboardextras",
    "dashboardattempts",
  ];

  const hasAnyDashboardChildAccess = () => {
    const hasAccess = pages.some(page => permissions[page] === true);
    return hasAccess;
  };


  return (

    <>

      {open && (

        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: sizeConfigs.sidebar.width,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              backgroundColor: themeStyles.sideBar.bg,
              borderRight: `1px solid ${themeStyles.sideBar.borderColor}`,
            }
          }}
        >
          <List
            disablePadding
            sx={{
              maxHeight: "calc(100vh - 50px)",
              overflowY: "auto",
            }}
          >
            <div className="flex w-full px-[10px] mt-[10px] justify-between items-center">
              <span className="text-[#167AC3] text-[20px] font-semibold">
                <img src="/images/logo/rd-rental-main-logo.png" alt="logo" width={60} height={60} />
              </span>
              <span className={`material-symbols-outlined text-[30px] ${themeStyles.sideBar.tailwindMenuItemsColor} hover:cursor-pointer`} onClick={() => {
                setOpen(!open);
                handleScreen();
              }}>
                arrow_left
              </span>
            </div>

            <div className="w-full flex pt-[10px] flex-col items-start gap-1.5">
              {appRoutes.map((route, index) => {
                if (isAdmin) {
                  return route.sidebarProps && route.child ? (
                    <SidebarItemCollapse item={route} key={index} />
                  ) : (
                    <SlidebarItemWithOutCollapse item={route} key={index} />
                  );
                }

                const hasAccessSection = route?.section ? permissions[route.section] : route.child ? route.child?.filter((child: any) => permissions[child.section] === true).length > 0 ? true : false : false;

                const isDashboard = route.path === "/dashboard";

                const dashboardHasAccess = isAdmin || (isDashboard ? hasAnyDashboardChildAccess() : hasAccessSection);

                if (dashboardHasAccess) {
                  return route.sidebarProps && route.child
                    ? <SidebarItemCollapse item={route} key={index} />
                    : <SlidebarItemWithOutCollapse item={route} key={index} />;
                }

                return null;
              })}

            </div>



          </List>

          {(isAdmin || showSettingsButton) && (
            <div className={`w-full mt-auto`}>
              <Divider className={`w-full ${themeStyles.divider.bg}`} />
              <div className="w-full flex flex-col items-start gap-1.5">
                <div className={`w-full flex p-[10px] items-center gap-2.5 cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`} onClick={handleNavigation}>
                  <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[25px]`}>
                    settings
                  </span>
                  <span className={`${themeStyles.sideBar.tailwindMenuItemsColor} text-[13px] tracking-wide`}>
                    Settings
                  </span>

                </div>
              </div>
            </div>
          )

          }

        </Drawer>
      )
      }

      {!open && (

        <div className={`sidebar close ${themeStyles.sideBar.tailwindBg} h-screen sticky top-0 flex flex-col border-r ${themeStyles.sideBar.tailwindBorderColor}`}>
          <div className={`flex items-center justify-center cursor-pointer mt-[10px] ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}>
            <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[30px]`}
              onClick={() => {
                setOpen(!open);
                handleScreen();
              }}>
              arrow_right
            </span>
          </div>

          <ul className="nav-links max-h-[calc(100vh - 50px)]">
            {appRoutes?.map((item: any, index: number) => {
              const hasCollapseAccessSection = item?.section ? isAdmin || permissions[item.section] : item.child ? item.child?.filter((child: any) => isAdmin || permissions[child.section] === true).length > 0 ? true : false : false;

              const isDashboard = item.path === "/dashboard";
              const dashboardHasAccess = isAdmin || (isDashboard ? hasAnyDashboardChildAccess() : hasCollapseAccessSection);

              if (dashboardHasAccess) {
                return (
                  item?.child
                    ?
                    <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}>
                      <div className="iocn-link">
                        <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px] `}>
                          {item?.sidebarProps?.icon}
                        </span>

                      </div>

                      <ul className={`sub-menu ${themeStyles.sideBar.tailwindBg} rounded border border-solid border-[#D0D0D0]`}>
                        <li className={`${themeStyles.sideBar.subMenuHeading} font-semibold cursor-default`}>
                          <span className="link_name">
                            {item?.sidebarProps?.displayText}
                          </span>
                        </li>

                        {item?.child?.map((childItem: any, i: number) => {
                          const hasAccessChild = childItem?.section ? (isAdmin || permissions[childItem.section]) : false;

                          if (hasAccessChild) {

                            return (
                              <li key={i} className={`${appState === childItem.state ? 'bg-active-rgba text-[#167AC3]' : 'bg-transparent ' + themeStyles.sideBar.tailwindMenuItemsColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor} font-medium cursor-pointer`}>
                                <Link to={childItem?.path}>
                                  {childItem?.sidebarProps?.displayText}
                                </Link>
                              </li>
                            )
                          }

                          return null;
                        })}
                      </ul>
                    </li>
                    :
                    <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}>
                      <span>
                        <Link to={item?.path}>
                          <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px]`}>
                            {item?.sidebarProps?.icon}
                          </span>
                        </Link>
                      </span>
                      <ul className={`sub-menu blank ${themeStyles.sideBar.tailwindBg} rounded border border-solid border-[#D0D0D0]`}>
                        <li className={`${appState === item.state ? 'bg-active-rgba text-[#167AC3]' : 'bg-transparent ' + themeStyles.sideBar.tailwindMenuItemsColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor} font-medium cursor-pointer`}>
                          <Link to={item?.path} className="link_name">
                            {item?.sidebarProps?.displayText}
                          </Link>
                        </li>
                      </ul>
                    </li>
                )
              }

              return null;
            })}


          </ul>

          {(isAdmin || showSettingsButton) && (
            <div className={`w-full mt-auto`}>
              <Divider className={`w-full ${themeStyles.divider.bg}`} />
              <div className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`} onClick={handleNavigation}>
                <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px]`}>
                  settings
                </span>
              </div>
            </div>
          )}
        </div>

      )}
    </>


  )
}